<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div class="edit-user-card">
        <button type="button" @click="closeModal" class="modal-close-btn">
          X
        </button>

        <div class="d-flex flex-column">
          <div class="task-title">{{ task.title }}</div>
          <p class="task-description mb-1">{{ task.description }}</p>

          <div class="row">
            <div class="col col-auto">
              <div class="d-flex task-company-name">{{ task.company.name }}</div>
            </div>
            <div class="col">
              <a :href="`/tasks/advanced?${advancedTaskParams}`" class="wfh-btn-link-primary">Advanced</a>
            </div>
          </div>
          <div class="row py-3">
            <div class="col-6 mb-3">
              <table>
                <tr>
                  <th>Due</th>
                </tr>
                <tr>
                  <td v-if="!editingTaskCompletion" @click="editContent('editingTaskCompletion')">
                    {{ estimatedTaskCompletionDate }}
                  </td>
                  <td v-if="editable && editingTaskCompletion">
                    <div class="date-wrapper">
                      <v-ctk-datepicker
                          format="YYYY-MM-DD"
                          formatted="YYYY-MM-DD"
                          v-model="estimatedCompletionDate"
                          :no-button-now="true"
                          :auto-close="true"
                          :no-label="true"
                          :only-date="true"
                      />
                      <img
                          v-bind:src="'/images/date.svg'"
                          alt
                          style="margin: 0 4px 4px 0"
                      />
                    </div>
                  </td>
                </tr>
              </table>
            </div>
            <div class="col-6 mb-3">
              <table>
                <tr>
                  <th>Hours Est.</th>
                </tr>
                <tr>
                  <td v-if="!editingTaskHour" @click="editContent('editingTaskHour')">{{ estimatedTaskHour }}</td>
                  <td v-if="editable && editingTaskHour">
                    <div class="input-group">
                      <input
                          ref="editingTaskHour"
                          id="task-hour-estimates"
                          type="number"
                          class="form-control"
                          placeholder="Hour Estimation"
                          v-model="taskCostEstimation"
                          @keyup.esc="stopEditing('editingTaskHour')"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">hours</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
            <div class="col-6">
              <table>
                <tr>
                  <th>Pay</th>
                </tr>
                <tr>
                  <td>{{ estimatedCost }}</td>
                </tr>
              </table>
            </div>
            <div class="col-6">
              <table>
                <tr>
                  <th>Sub-tasks</th>
                </tr>
                <tr>
                  <td>{{ totalSubtasksText() }}</td>
                </tr>
              </table>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col">
              <div class="row">
                <div class="col">
                  <p class="subtask-header">Sub Tasks</p>
                </div>
                <div class="col text-right" v-if="editable && !isCompleted">
                  <button type="button" class="link-btn ml-auto" @click="addSubtask">
                    <i class="fas fa-plus mr-2"></i>Add
                  </button>
                </div>
              </div>

              <div class="subtask-container" v-if="hasSubtasks()">
                <div v-for="subtask in subtasks">
                  <subtask
                      :key="`subtask-${subtask.id}`"
                      :subtask="subtask"
                      :editable="editable && !isCompleted"
                      @deleteSubtask="deleteSubtask"
                  ></subtask>
                </div>
              </div>
              <div class="text-center row" v-else>
                <div class="col">
                  <p class="font-weight-normal m-0">No Subtask</p>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <input
                      ref="newSubtaskTitle"
                      type="text"
                      class="form-control"
                      placeholder="Enter subtask title..."
                      v-show="isAddingNew"
                      v-model="newSubtaskTitle"
                      @keydown.enter="submitNewSubtask()"
                      @keydown.esc="stopEditingSubtask()"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center mt-3" v-if="hasCTA">
          <button class="btn wfh-btn-primary flex-grow-1 w-auto" @click="$emit('submit')">
            {{ actionText }}
          </button>
          <button
              class="btn wfh-btn-primary-outline ml-2 w-auto"
              v-if="displaySaveBtn && !isCompleted"
              @click="saveChanges"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OpenTaskDetailsModal',

  props: {
    task: {
      type: Object,
      required: true,
    },
    actionText: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hasCTA: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      editingTaskCompletion: false,
      editingTaskHour: false,
      estimatedCompletionDate: this.task.task_cost?.estimated_completion_date
          ? this.task.task_cost.estimated_completion_date
          : null,
      newTaskDetails: {},
      subtasks: [],
      newSubtaskTitle: '',
      isAddingNew: false,
      taskCostEstimation: this.task.task_cost?.hour_estimation ?? null,
    };
  },

  watch: {
    'estimatedCompletionDate' () {
      this.newTaskDetails['estimated_completion_date'] = this.estimatedCompletionDate;
    },

    'taskCostEstimation' () {
      this.newTaskDetails['hour_estimation'] = Number(this.taskCostEstimation);
    },
  },

  computed: {
    estimatedTaskCompletionDate () {
      return this.estimatedCompletionDate ?? 'N/A';
    },

    estimatedTaskHour () {
      return this.taskCostEstimation ? `${this.taskCostEstimation} Hours` : 'N/A';
    },

    estimatedCost () {
      return this.task.task_cost?.cost_estimation
          ? `${this.task.task_cost?.payment_currency} ${this.task.task_cost?.cost_estimation}`
          : 'N/A';
    },

    displaySaveBtn () {
      return this.editingTaskCompletion || this.editingTaskHour;
    },

    isCompleted () {
      return this.task.completed_at !== null;
    },

    advancedTaskParams () {
      const params = {
        title: this.title,
        type: this.task.type ?? 'metric',
        completed_at: this.task.completed_at,
      };

      if (this.task.id) {
        params.id = this.task.id;
      }

      return Object.keys(params).map(key => {
        if (params[key]) {
          return `${key}=${encodeURIComponent(params[key])}`;
        }

        return '';
      }).join('&');
    },
  },

  methods: {
    totalSubtasksText () {
      const total = this.task.total_subtasks ?? 0;

      return `${total} task${total > 1 ? 's' : ''}`;
    },

    hasSubtasks () {
      return this.task.total_subtasks > 0;
    },

    closeModal (evt) {
      if (evt && evt.keyCode === 27) {
        this.$emit('close');
      } else if (evt && evt.type === 'click') {
        this.$emit('close');
      }
    },

    resetEditing () {
      this.editingTaskHour = false;
      this.editingTaskCompletion = false;
    },

    async saveChanges () {
      try {
        const res = await axios.put(`/tasks/${this.task.id}`, this.newTaskDetails);
        this.task.task_cost = res.data.data.task_cost;
        this.resetEditing();
      } catch (e) {
        this.resetEditing();
        this.$emit('close');
      }
    },

    editContent (item) {
      if (!this.isCompleted) {
        this[item] = this.editable;

        this.$nextTick(() => {
          if (this.$refs[item]) {
            this.$refs[item].focus();
          }
        });
      }
    },

    async fetchSubtasks () {
      const res = await axios.get(`/task/subtasks?ids=${this.task.id}`);

      this.subtasks = res.data.data[this.task.id] ?? [];
    },

    addSubtask () {
      this.isAddingNew = true;

      this.$nextTick(() => {
        this.$refs['newSubtaskTitle'].focus();
      });
    },

    async submitNewSubtask () {
      if (this.newSubtaskTitle) {
        const res = await axios.post(`/tasks/${this.task.id}/subtasks`, {
          subtasks: [
            {
              title: this.newSubtaskTitle,
              is_completed: false,
            },
          ],
        });

        (new Promise((resolve, reject) => {
          this.subtasks.push(res.data.data[0]);
          this.task.total_subtasks++;
          resolve();
        }).then(this.scrollSubtasksToBottom));
      }

      this.newSubtaskTitle = '';
    },

    async deleteSubtask (subtaskId) {
      const res = await axios.delete(`/tasks/${subtaskId}`);
      this.subtasks = this.subtasks.filter(subtask => {
        return subtask.id !== subtaskId;
      });
      this.task.total_subtasks--;
    },

    stopEditingSubtask () {
      this.newSubtaskTitle = '';
      this.isAddingNew = false;
    },

    stopEditing (item) {
      if (item === 'editingTaskHour') {
        this.editingTaskHour = false;
      } else {
        this.editingTaskCompletion = false;
      }
    },

    scrollSubtasksToBottom () {
      const container = document.querySelector('.subtask-container');
      container.scrollTop = container.scrollHeight + 60;
    },
  },

  mounted () {
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;

    if (!this.editable) {
      document.addEventListener('keyup', this.closeModal);
    }

    this.fetchSubtasks();
  },

  beforeDestroy () {
    const scrollY = document.body.style.top;

    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  },
};
</script>

<style scoped lang="scss">
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
  overflow-y: auto;
}

.task-title {
  color: #303030;
  font-family: "Open Sans", sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 32px;
}

.task-description {
  color: #606064;
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
  font-weight: 400;
  white-space: pre-wrap;
  width: 100%;
  overflow: auto;
  max-height: 120px;
}

th {
  color: #808fa4;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 22px;
}

td {
  color: #303030;
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
  font-weight: 400;
}

.subtask-header {
  color: #303030;
  font-family: Lato, serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 28px;
}

.task-company-name {
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 22px;
  align-items: center;

  &:before {
    content: ' ';
    height: 8px;
    width: 8px;
    border-radius: 50%;
    background-color: #2ecc71;
    margin-right: 8px;
  }
}

.subtask-container {
  max-height: 120px;
  overflow-y: scroll;
  overflow-x: hidden;
}

.cost-wrapper {
  select {
    border: 1px solid #ced4da;
  }

  input {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.date-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #eff1f5;
  padding: 0 12px 0 0;

  .datepicker {
    border: none;

    &:focus {
      box-shadow: none;
    }
  }
}

.link-btn {
  display: flex;
  align-items: center;
  color: #303030;
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 22px;
  border: none;
  outline: none;
  background-color: transparent;

  .fas {
    color: #3593fe !important;
  }
}
</style>
